import React, { useState, useEffect } from 'react';
import { getUnavailablePricingOptions } from '../../../ShoppingCart/ShoppingCartService';
import { format } from 'date-fns';

// Helper to format date consistently - use ISO format for comparison
const formatDateForComparison = (dateString: string): string => {
  return format(new Date(dateString), 'yyyy-MM-dd');
};

// Helper to format date for display - use dd.MM.yyyy format
const formatDateForDisplay = (dateString: string): string => {
  return format(new Date(dateString), 'dd.MM.yyyy');
};

// Create date range key for comparison (ISO format)
const createDateRangeKey = (startDate: string, endDate: string): string => {
  return `${formatDateForComparison(startDate)}|${formatDateForComparison(endDate)}`;
};

// Create date range for display (DD.MM.YYYY format)
const createDateRangeDisplay = (startDate: string, endDate: string): string => {
  return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
};

interface DateRange {
  startDate: string;
  endDate: string;
}

interface PriceOption {
  description: string;
  price: number;
  id: number;
  dateRanges: DateRange[];
}

interface DatePriceModalProps {
  dates: string[];
  priceOptions: PriceOption[];
  onClose: () => void;
  onAddToCart: (selectedDate: string, selectedPrice: string, priceOptionId: number, dateRange: DateRange) => void;
  unavailableDates: string[];
  packageId: number; // Add packageId prop
}

const DatePriceModal: React.FC<DatePriceModalProps> = ({ dates, priceOptions, onClose, onAddToCart, unavailableDates, packageId }) => {
  const uniqueDates = Array.from(new Set(dates)) ;
 // const uniquePriceOptions = Array.from(new Set(priceOptions.map(option => `${option.description}: €${option.price.toFixed(2)}`)));

  const [selectedDate, setSelectedDate] = useState('');
  const [selectedPriceOption, setSelectedPriceOption] = useState('');
  const [unavailablePricingOptions, setUnavailablePricingOptions] = useState<{ [key: string]: { pricingModelId: number, description: string }[] }>({});
  const [allOptionsUnavailable, setAllOptionsUnavailable] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  useEffect(() => {
    const fetchUnavailablePricingOptions = async () => {
      if (selectedDate) {
        // Find the date range using the consistent display format
        const dateRange = priceOptions.flatMap(option => option.dateRanges).find(range => 
          createDateRangeDisplay(range.startDate, range.endDate) === selectedDate
        );
        if (dateRange) {
          console.log('Fetching unavailable pricing options with dateRange:', dateRange);
          const unavailableOptions = await getUnavailablePricingOptions(packageId, [dateRange]);
          const unavailableMap = unavailableOptions.data.reduce((acc, { startDate, endDate, pricingModelId, description }) => {
            // Use consistent format for the map key
            const formattedDateRange = createDateRangeDisplay(startDate, endDate);
            if (!acc[formattedDateRange]) acc[formattedDateRange] = [];
            acc[formattedDateRange].push({ pricingModelId, description });
            return acc;
          }, {} as { [key: string]: { pricingModelId: number, description: string }[] });
          setUnavailablePricingOptions(unavailableMap);
          console.log('unavailablePricingOptions:', unavailableMap);

          // Check if all options are unavailable
          const isAllUnavailable = priceOptions.every(option => {
            return option.dateRanges.some(range => {
              const dateRangeKey = createDateRangeDisplay(range.startDate, range.endDate);
              return unavailableMap[dateRangeKey]?.some(unavailableOption => unavailableOption.pricingModelId === option.id);
            });
          });
          setAllOptionsUnavailable(isAllUnavailable);
        }
      }
    };

    fetchUnavailablePricingOptions();
  }, [selectedDate, packageId, priceOptions]);

  const handleAddToCart = () => {
    const priceOption = priceOptions.find(option => `${option.description}: €${option.price.toFixed(2)}` === selectedPriceOption);
    const dateRange = priceOption?.dateRanges.find(range => 
      createDateRangeDisplay(range.startDate, range.endDate) === selectedDate
    );
    if (priceOption && dateRange) {
      onAddToCart(selectedDate, selectedPriceOption, priceOption.id, dateRange);
    }
  };

  return (
    <div className="date-price-modal">
      <div className="date-price-modal__content">
        <button className="date-price-modal__content__close" onClick={onClose}>✕</button>
        <h3>Select Date and Price</h3>
        <div className='date-price-modal__content__info'><small>All Prices In € Per Person</small></div>
        
        <select className="date-price-modal__content__select" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>
          <option value="" disabled>Select a date range</option>
          {uniqueDates.map((date, index) => (
            <option key={index} value={date} disabled={unavailableDates.includes(date)}>{date}</option>
          ))}
        </select>
        
        {allOptionsUnavailable && selectedDate && (
          <div className="date-price-modal__content__sold-out">This package is sold out for the selected date.</div>
        )}

        <select className="date-price-modal__content__select" value={selectedPriceOption} onChange={(e) => setSelectedPriceOption(e.target.value)} disabled={!selectedDate || allOptionsUnavailable}>
          <option value="" disabled>Select a price option</option>
          {priceOptions.map((option, index) => {
            const isUnavailable = option.dateRanges.some(range => {
              const dateRangeKey = createDateRangeDisplay(range.startDate, range.endDate);
              return unavailablePricingOptions[dateRangeKey]?.some(unavailableOption => unavailableOption.pricingModelId === option.id);
            });
            return (
              <option key={index} value={`${option.description}: €${option.price.toFixed(2)}`} disabled={isUnavailable}>
                {option.description}: €{option.price.toFixed(2)}
              </option>
            );
          })}
        </select>
        <button 
          className="date-price-modal__content__add-button" 
          onClick={handleAddToCart} 
          disabled={!selectedDate || !selectedPriceOption || allOptionsUnavailable}
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
};

export default DatePriceModal;
